import {
  normalizeSpokenText,
  type SpokenPickExtractionInput,
  type SpokenPickExtractionPort,
} from '@/shared-responder-v2-core';

import { SPOKEN_PICK_CACHE_LIMIT } from './shared-responder-v2-spoken-pick.consts';

export interface SpokenPickQueryResult {
  readonly data?: {
    readonly formattedTranscriptAnswer: {
      readonly extractedOptions?: readonly string[] | null;
    };
  } | null;
  readonly error?: unknown;
}

type SpokenPickQueryRunner = (
  input: SpokenPickExtractionInput
) => Promise<SpokenPickQueryResult>;

function toCacheKey(input: SpokenPickExtractionInput): string {
  return JSON.stringify([
    input.shape,
    input.options.map((option) => option.value),
    normalizeSpokenText(input.transcript),
  ]);
}

function toExtractedOptions(
  result: SpokenPickQueryResult
): readonly string[] | null {
  const answer = result.data?.formattedTranscriptAnswer;
  const failed = result.error !== undefined && result.error !== null;

  return answer === undefined || failed
    ? null
    : (answer.extractedOptions ?? []);
}

async function runSafely(
  run: SpokenPickQueryRunner,
  input: SpokenPickExtractionInput
): Promise<readonly string[] | null> {
  try {
    return toExtractedOptions(await run(input));
  } catch {
    return null;
  }
}

export function createSpokenPickExtractionPort(
  run: SpokenPickQueryRunner
): SpokenPickExtractionPort {
  const cache = new Map<string, readonly string[]>();

  function remember(key: string, extracted: readonly string[]): void {
    cache.set(key, extracted);

    if (cache.size > SPOKEN_PICK_CACHE_LIMIT) {
      const [oldestKey] = cache.keys();

      if (oldestKey !== undefined) {
        cache.delete(oldestKey);
      }
    }
  }

  return {
    isAvailable: true,
    extract: async (input) => {
      const key = toCacheKey(input);
      const cached = cache.get(key);

      if (cached !== undefined) {
        return cached;
      }

      const extracted = await runSafely(run, input);

      if (extracted !== null) {
        remember(key, extracted);
      }

      return extracted;
    },
  };
}
